import React, { useState, useEffect } from "react";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { PropTypes } from "prop-types";
import ErrorComponent from "../../../../../../common/ErrorComponent";
import css from "../../common/anomalyMagneticStyle.less";
import useI18n from "../../../../../../i18n/useI18n";
import NoData from "../../../../../../common/table/NoData";
import SettingsDrawer from "../../../../../../common/SettingsDrawer";
import OverLappingSpinner from "../../../../../../common/Spinner/OverLappingSpinner";
const [HbrTable, HbrIcon, HbrPagination, HbrInput, HbrButton] = reactWrapper([
  "hbr-table",
  "hbr-icon",
  "hbr-pagination",
  "hbr-input",
  "hbr-button",
]);

const ADTableView = ({ getSelectedRows = () => {}, ...props }) => {
  const { tViews } = useI18n("views");
  const { source, columns, query, count, selected } =
    props.tableStore.getState();

  const { search, pageLength, page } = query;

  const onSearch = (e) => {
    props.tableStore.setQuery({ search: e.target.value });
  };

  const [, forceUpdate] = useState(null);
  useEffect(() => {
    const unsubscribe = props.tableStore.subscribe(forceUpdate);
    return () => unsubscribe();
  }, [props.tableStore]);

  useEffect(() => {
    selected.length > 5
      ? getSelectedRows(selected.slice(0, 5), source)
      : getSelectedRows(selected, source);
  }, [selected]);

  return (
    <div className={css[`ad-TableView`]}>
      <div className={`ad-table-header`}>
        <div className="hbr-css__layout-row-xs">
          <div
            className="hbr-css__text-heading-lg-bold"
            role="heading"
            aria-level={"2"}
          >
            {props.header}
          </div>
          <div className="hbr-css__text-secondary-xs">{`${count} ${props.entity}`}</div>
        </div>
      </div>
      <div className="ad-search-and-export">
        <HbrInput
          id="hbr-search-field"
          aria-label={tViews("searchTable")}
          placeholder={tViews("c360.tSearch")}
          onHbr-clear={onSearch}
          clearable={true}
          onkeydown={(e) => {
            if (e.code === "Enter") onSearch(e);
          }}
          value={search}
        >
          <HbrIcon slot="prefix" name="magnifying-glass" sentiment="neutral" />
        </HbrInput>
        {Number.isFinite(count) && count > 0 && (
          <HbrButton
            id="hbr-export-button"
            variant="outline"
            onClick={() => {
              props.tableStore.setQuery({ export: "export" });
            }}
          >
            <HbrIcon slot="prefix" sentiment="interact" name="upload-simple" />
            {tViews("export")}
          </HbrButton>
        )}
      </div>
      <div className="harborTableView">
        <HbrTable
          id={props.id}
          data-testid={props.id}
          aria-label={props.header}
          headerSlot={true}
          serverSide={true}
          source={source}
          columns={columns}
          canFocus={false}
          resize
          externalSorting={true}
          onHbr-before-sort={(event) => {
            const { column, order } = event.detail;
            let sortHook = props.tableStore.hooks[0];
            if (query.order[0][0] === column.prop) {
              if (query.order[0][1] === "desc") {
                sortHook.sort({ order: [[column.id, "asc"]] });
              } else {
                sortHook.sort({ order: [[column.id, "desc"]] });
              }
            } else {
              sortHook.sort({ order: [[column.id, order]] });
            }
          }}
        >
          <div className="hbr-css__table-empty-state">
            {props.status.isLoading ? (
              <OverLappingSpinner />
            ) : props.status.error ? (
              <div role="alert">
                <ErrorComponent
                  {...props.status.error}
                  className="small-dashlet-error"
                  width="220px"
                />
              </div>
            ) : (
              source.length === 0 && (
                <NoData
                  count={count}
                  loader={props.status?.isLoading}
                  error={props.status?.error || null}
                  text={
                    props.message ? props.message : tViews("logs.emptyData")
                  }
                />
              )
            )}
          </div>
        </HbrTable>
        {props.isPagination && (
          <HbrPagination
            id="ad-pagination"
            aria-label={tViews("a11yLabels.paginationAriaLabel")}
            pageSize={pageLength}
            itemCount={count}
            page={page}
            showAll
            orderArray={[1, 2, 0]}
            sizeChanger={true}
            onHbr-size-changed={(e) => {
              props.tableStore.setQuery({
                pageLength: e.detail.pageSize,
                page: 0,
              });
            }}
            onHbr-page-changed={(e) => {
              props.tableStore.setQuery({ page: e.detail });
            }}
          />
        )}
        {props.tableSettings && (
          <SettingsDrawer
            tableStore={props.tableStore}
            localStorageName={props.tableSettings.localStorageName}
            defaultToggles={props.tableSettings.defaultToggles}
            dataTestid={props.tableSettings.dataTestid}
          />
        )}
      </div>
    </div>
  );
};

ADTableView.propTypes = {
  header: PropTypes.string,
  status: PropTypes.object,
  tableStore: PropTypes.object.isRequired,
  defaultSort: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  id: PropTypes.string.isRequired,
  isPagination: PropTypes.bool,
  entity: PropTypes.string,
  getSelectedRows: PropTypes.func,
  message: PropTypes.string,
  tableSettings: PropTypes.shape({
    localStorageName: PropTypes.string,
    defaultToggles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    dataTestid: PropTypes.string,
  }),
};

export default ADTableView;
